import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import {
  client,
  MinimalEntityDto,
  ModelsControllerListSummariesData,
} from "@/client";
import {
  FormSelectInputItem,
  FormSelectInputItemCommonProps,
} from "@/components/custom/form-inputs/select-input/select-input";
import { useOptionsMapping } from "@/components/custom/form-inputs/select-input/common";
import { useDataTableFiltersSubmit } from "@/components/custom/data-table/buttons/data-table-filters";
import { useFilterIsActive } from "@/components/custom/data-table/data-table-context";
import { FormSelectInputAsyncPaginateQueryCreatableItem } from "../form-inputs/select-input/select-input-async-pagination-creatable";
import { withPermission } from "@/components/custom/with-permission";
import { listPermissions } from "@/lib/list-permissions";
type TForm = ModelsControllerListSummariesData;

const NameFilter = withPermission(listPermissions.models, NameFilterControl);
function NameFilterControl(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const perPage = 10;
  return (
    <FormSelectInputAsyncPaginateQueryCreatableItem
      form={form}
      name="name"
      label={t("modelName")}
      isMulti={false}
      isClearable={true}
      container={{ className: "flex-1 min-w-[150px]" }}
      mapping={(v) => ({ value: v!, label: v as unknown as string })}
      queryKey={client.models.listMinimal.queryKey} // cache
      queryCreator={client.models.listMinimal.asQueryOptions<any>}
      createOptions={(data) =>
        data.results.map((row: MinimalEntityDto) => ({
          label: [row.name, row.id ? ` (${row.id})` : undefined]
            .filter(Boolean)
            .join(" "),
          value: row.name!,
        }))
      }
      hasMore={(data, variable) =>
        data.results.length >= perPage && data.total > variable.page * perPage
      }
      requestVariable={(search, page, limit) => ({
        search,
        limit: limit ?? perPage,
        page,
      })}
      isActive={useFilterIsActive("name")}
      {...props}
    />
  );
}

function TypeFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const options = [
    { value: undefined as any, label: "Any" },
    { value: "classifier", label: "Classifier" },
    { value: "objectDetector", label: "Object Detector" },
    { value: "embeddingFilter", label: "Embedding Filter" },
    { value: "any", label: "Any" },
  ];
  const mapping = useOptionsMapping(options);

  return (
    <FormSelectInputItem
      form={form}
      name="type"
      label={t("type")}
      mapping={mapping as any}
      options={options}
      {...props}
      isMulti={false}
      container={{ className: "flex-1 min-w-[150px]" }}
      isActive={useFilterIsActive("type")}
    />
  );
}

function RoleFilter(props: FormSelectInputItemCommonProps) {
  const { t } = useTranslation();
  const form = useFormContext<TForm>();
  const options = [
    { value: undefined as any, label: "Any" },
    { value: "calibrationDetector", label: "Calibration Detector" },
    { value: "nonScanDetector", label: "Non-Scan Detector" },
    { value: "objectDetector", label: "Object Detector" },
    { value: "predictor", label: "Predictor" },
    { value: "similarityFilter", label: "Similarity Filter" },
    { value: "any", label: "Any" },
  ];
  const mapping = useOptionsMapping(options);

  return (
    <FormSelectInputItem
      form={form}
      name="role"
      label={t("role")}
      mapping={mapping as any}
      options={options}
      {...props}
      isMulti={false}
      container={{ className: "flex-1 min-w-[150px]" }}
      isActive={useFilterIsActive("role")}
    />
  );
}

export function ModelSelectionFilters() {
  return (
    <div className="flex gap-2 flex-col">
      <NameFilter />
      <TypeFilter />
      <RoleFilter />
    </div>
  );
}

export function ModelSelectionFiltersInline() {
  const submit = useDataTableFiltersSubmit();

  return (
    <>
      <NameFilter
        container={{
          clear: null,
          className: "space-y-0",
          sideBySide: false,
          size: "sm",
        }}
        classNames={{ control: (_s) => "py-0 h-8 !min-h-8 min-w-80" }}
        inline={true}
        displaySelected={true}
        onChange={submit}
        controlShouldRenderValue={true}
      />
    </>
  );
}
